import {
  Component,
  inject,
  input,
  model,
  output,
  computed,
  booleanAttribute,
  signal,
  WritableSignal
} from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { IdService } from '../../services/id.service';
import type { ControlValueAccessor } from '@angular/forms';
import type { InputSignal, ModelSignal, OutputEmitterRef, Signal, InputSignalWithTransform } from '@angular/core';

export type Position = 'left' | 'right' // TODO convert to direction agnostic start/end pair

export type CheckboxStyle = 'default' | 'toggle'

export type SelectorPosition = 'center' | 'top'

// TouchTarget.PARENT needs 'position: relative;' on parent clickable element
export type TouchTarget = 'self' | 'parent'

/**
 * Reference implementation for the core dual-support control shape.
 * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 *
 * - `checked` is the single source of truth. It is what the template renders,
 *   what `writeValue()` writes, and what user interaction updates.
 * - Because it is a `model()` named `checked`, this component already satisfies
 *   Angular Signal Forms' native custom-control contract.
 * - The `ControlValueAccessor` implementation is a thin adapter over that model,
 *   required only by `[(ngModel)]` / `[formControl]`. It can be deleted once no
 *   template-driven consumers remain, without breaking `[formField]` consumers.
 */
@Component({
  selector: 'td-wb-checkbox',
  templateUrl: 'checkbox.component.html',
  styleUrls: ['checkbox.component.scss'],
  providers: [ValueAccessorProviderFactory(CheckboxComponent)],
  host: {
    '[class.td-wb-checkbox__touch-target-parent]': 'touchTarget() === "parent"'
  }
})
export class CheckboxComponent implements ControlValueAccessor {
  private static readonly ID_PREFIX: string = 'td-wb-checkbox';
  private readonly idService = inject(IdService);

  readonly checkboxId: InputSignal<string | undefined> = input<string | undefined>();
  readonly optionValue: InputSignal<unknown> = input<unknown>(undefined, { alias: 'value' });
  readonly name: InputSignal<string | undefined> = input<string | undefined>();
  readonly tabIndex: InputSignal<string | undefined> = input<string | undefined>();
  readonly labelPosition: InputSignal<Position | undefined> = input<Position | undefined>();
  readonly checkboxStyle: InputSignal<CheckboxStyle | undefined> = input<CheckboxStyle | undefined>();
  readonly selectorPosition: InputSignal<SelectorPosition> = input<SelectorPosition>('top');
  readonly touchTarget: InputSignal<TouchTarget> = input<TouchTarget>('self');
  readonly isFullWidth: InputSignal<boolean> = input<boolean>(false);
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly ariaDescribedBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-describedby' });

  readonly disabledInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, { alias: 'disabled', transform: booleanAttribute });
  private readonly disabledByOwner: WritableSignal<boolean> = signal(false);
  /** Signal Forms writes both the `disabled` input and `setDisabledState()`. Reconcile, never pick one. */
  readonly disabled: Signal<boolean> = computed(() => this.disabledInput() || this.disabledByOwner());

  /** Single source of truth, and the Signal Forms native custom-control binding. */
  readonly checked: ModelSignal<boolean> = model<boolean>(false);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  private readonly _generatedId: string = this.idService.generateNamespaceSequentialId(CheckboxComponent.ID_PREFIX);
  readonly finalCheckboxId: Signal<string> = computed((): string => this.checkboxId() ?? this._generatedId);
  readonly finalName: Signal<string> = computed((): string => this.name() ?? `name-${this.finalCheckboxId()}`);

  readonly labelOnTheLeft: Signal<boolean> = computed((): boolean => this.labelPosition() === 'left');
  readonly toggleStyle: Signal<boolean> = computed((): boolean => this.checkboxStyle() === 'toggle');

  hasAriaLabel: Signal<boolean> = computed((): boolean => !!(this.ariaLabel() || this.ariaLabelledBy()));

  private onChangeCallback: (value: boolean) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  onChange(event: Event): void {
    event.stopPropagation();
  }

  onInputClick(event: Event): void {
    event.stopPropagation();
    if (!this.disabled()) {
      this.toggle();
      this.notifyUserInteraction(this.checked());
    }
  }

  toggle(): void {
    this.checked.update((checked: boolean): boolean => !checked);
  }

  // ControlValueAccessor: owner -> control. Must never emit onChangeCallback.
  writeValue(value: unknown): void {
    this.checked.set(Boolean(value));
  }

  // ControlValueAccessor: control -> owner.
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChangeCallback = fn;
  }

  // ControlValueAccessor: control -> owner.
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  // ControlValueAccessor: owner -> control.
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  private notifyUserInteraction(value: boolean): void {
    this.onChangeCallback(value);
    this.onTouchedCallback();
    this.touch.emit();
  }
}
